import { useState } from "react";
import { Loader2, Eye, ExternalLink } from "lucide-react";
import { getBackendUrl } from "@/lib/backend";

const BACKEND = getBackendUrl();

/**
 * Embedded `<model-viewer>` panel used inside the CAD dialog's job card.
 *
 * The poster image is rendered ABSOLUTE underneath so the viewer never
 * goes blank during the three.js warm-up. When `meshObjectUrl` flips to
 * a real blob URL, the interactive 3D scene fades in on top. The
 * loading pill auto-disappears once the web-component has upgraded AND
 * the blob is present. On preview failure we surface a retry button
 * while keeping the .glb download path working.
 *
 * `job.rendered_image_url` may be a relative path (backend-hosted, new
 * jobs) or an absolute Tripo CDN URL (legacy, may have expired). We
 * normalise via the `posterUrl` derivation and gracefully hide the
 * poster `<img>` on load error so the viewer's own loading state shows.
 */
export default function MeshViewer3D({
  job,
  meshObjectUrl,
  viewerReady,
  meshLoading,
  meshError,
  meshProgress,
  onRetry,
}) {
  const [posterError, setPosterError] = useState(false);
  const rawPoster = job?.rendered_image_url;
  const posterUrl = rawPoster
    ? (rawPoster.startsWith("http") ? rawPoster : `${BACKEND}${rawPoster}`)
    : null;
  const showPoster = posterUrl && !posterError;
  return (
    <div
      data-testid="cad-mesh-viewer"
      className="mt-4 relative rounded-xl overflow-hidden border border-[#D4AF37]/20 bg-[#0A0A0A]"
      style={{ height: 360 }}
    >
      {/* Tripo's pre-baked preview — always rendered as the immediate
          visual so the box never goes blank while three.js is
          bootstrapping. Hide it on load error so legacy jobs with
          expired Tripo URLs degrade gracefully. */}
      {showPoster && (
        <img
          src={posterUrl}
          alt="3D mesh preview"
          onError={() => setPosterError(true)}
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          style={{ filter: "brightness(0.95)" }}
        />
      )}
      {/* Render <model-viewer> ON TOP. Transparent until it loads the
          .glb, then it covers the poster with the interactive scene.

          Wrapped in a pointer-events isolation layer for two reasons:
          1. Radix Dialog's DismissableLayer captures pointerdown at
             the container level to detect outside-clicks. When
             model-viewer calls `setPointerCapture` on its shadow-DOM
             canvas, Radix can release/interfere with that capture,
             breaking drag-to-rotate mid-drag. `stopPropagation` on
             pointer down/move/up keeps the events inside model-viewer.
          2. `touch-action: none` — DialogContent has `overflow-y-auto`
             which turns any downward touch-drag into vertical scroll
             instead of a camera-orbit gesture. Disabling touch-action
             here reclaims the drag for model-viewer.
          `user-select: none` prevents drag-selecting text under the
          canvas which can also confuse the browser's drag semantics.
       */}
      {meshObjectUrl && (
        <div
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          className="absolute inset-0"
          style={{ touchAction: "none", userSelect: "none" }}
        >
          <model-viewer
            src={meshObjectUrl}
            alt="Generated 3D mesh preview"
            poster={showPoster ? posterUrl : undefined}
            camera-controls
            touch-action="none"
            auto-rotate
            auto-rotate-delay="600"
            rotation-per-second="22deg"
            interaction-prompt="none"
            exposure="0.85"
            shadow-intensity="0.6"
            environment-image="neutral"
            style={{
              width: "100%", height: "100%",
              backgroundColor: "transparent",
            }}
          />
        </div>
      )}
      {meshError && !meshObjectUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="max-w-xs text-center space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300/85">Preview unavailable</div>
            <div className="text-xs text-white/75">{meshError}</div>
            <button
              type="button"
              onClick={onRetry}
              data-testid="mesh-preview-retry"
              className="mt-1 inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/45 text-[#D4AF37] hover:bg-[#D4AF37]/30 transition"
            >
              <Loader2 className={`w-3 h-3 ${meshLoading ? "animate-spin" : ""}`} />
              Retry preview
            </button>
            <div className="text-[10px] text-white/45">
              The .glb is still safe — use the Download button below.
            </div>
          </div>
        </div>
      )}
      {!meshError && (!meshObjectUrl || !viewerReady) && (
        <div className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 bg-black/55 backdrop-blur px-2 py-1 rounded-full border border-white/10 text-[10px] text-white/70">
          <Loader2 className="w-2.5 h-2.5 text-[#D4AF37] animate-spin" />
          {meshLoading
            ? (meshProgress > 0
                ? `Fetching mesh · ${meshProgress}%`
                : meshProgress < 0
                  ? `Fetching mesh · ${-meshProgress} MB`
                  : "Fetching mesh…")
            : meshObjectUrl
              ? "Starting 3D engine…"
              : "Loading preview…"}
        </div>
      )}
      <div className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-[#D4AF37]/85 bg-black/60 backdrop-blur px-2 py-1 rounded-full border border-[#D4AF37]/25">
        <Eye className="w-2.5 h-2.5" /> Live 3D preview · drag to rotate
      </div>
      <a
        href={`/3d-viewer/${job.job_id}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="cad-mesh-open-new-tab"
        title="Open the 3D mesh in a fullscreen viewer (new tab)"
        className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-white/75 hover:text-[#D4AF37] bg-black/60 hover:bg-black/80 backdrop-blur px-2 py-1 rounded-full border border-white/15 hover:border-[#D4AF37]/45 transition"
      >
        <ExternalLink className="w-2.5 h-2.5" /> Open in new tab
      </a>
    </div>
  );
}
